<script setup>
import { Inertia } from "@inertiajs/inertia";
import { ref, onMounted, watch } from "vue";
import { useForm } from "@inertiajs/inertia-vue3";
import WidgetLayout from "@/Layouts/WidgetLayout.vue";
import Label from "@/Components/Settings/Label.vue";
import Input from "@/Components/Settings/Input.vue";
import Textarea from "@/Components/Textarea.vue";
import RadioButtons from "@/Components/Widget/RadioButtons.vue";

const props = defineProps({
  office: Object,
  type: Object,
  practitioner: Object,
  insurance: Number,
  video: Boolean,
  pain: Boolean,
  widgetkey: String,
  widgeteid: String,
  timeslot: Object,
  errors: Object,
});

const errors = ref(props.errors);

const form = useForm({
  type_id: props.type.id,
  practitioner_id: props.practitioner.id,
  insurance: props.insurance,
  video: props.video,
  pain: props.pain,
  widgeteid: props.widgeteid,
  timeslot: props.timeslot,

  firstname: "",
  lastname: "",
  birthday: "",
  email: "",
  tel: "",
  gender: null,
  is_first_visit: null,
  notes: "",
});
const backUrl = ref("/");
const days = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

let pymChild = null;

onMounted(() => {
  //console.log("props", props);

  if (typeof pym !== "undefined") {
    pymChild = new pym.Child({ id: props.widgeteid });
  }
});

// watch form
// watch(
//   () => form,
//   (form) => {
//     console.log("form", form);
//   },
//   { deep: true, immediate: true }
// );
watch(
  () => props.errors,
  (errors) => {
    if (pymChild) {
      setTimeout(() => {
        pymChild.sendHeight();
      }, 200);
    }
  },
  { deep: true }
);

function formatDate(inputDate, timezone = true) {
  //console.log("formatDate", inputDate);
  let date = new Date(inputDate);

  if (timezone) {
    let utc = date.getTime() + date.getTimezoneOffset() * 60000; // deal with timezone in milliseconds
    date = new Date(utc);
  }

  let weekday = days[date.getDay()],
    day = date.getDate().toString().padStart(2, "0"),
    month = (date.getMonth() + 1).toString().padStart(2, "0"),
    year = date.getFullYear();

  return `${weekday}, ${day}.${month}.${year}`;
}

function formatTime(inputDate, timezone = true) {
  let date = new Date(inputDate);

  if (timezone) {
    let utc = date.getTime() + date.getTimezoneOffset() * 60000; // deal with timezone in milliseconds
    date = new Date(utc);
  }

  let hours = date.getHours().toString().padStart(2, "0"),
    minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

function submitForm() {
  // validate form
  let hasErrors = false;
  if (!form.firstname) {
    errors.value.firstname = "Bitte Vorname eingeben";
    hasErrors = true;
  }
  if (!form.lastname) {
    errors.value.lastname = "Bitte Nachname eingeben";
    hasErrors = true;
  }
  if (!form.birthday) {
    errors.value.birthday = "Bitte Geburtstag eingeben";
    hasErrors = true;
  }
  if (!form.email) {
    errors.value.email = "Bitte E-Mail eingeben";
    hasErrors = true;
  }
  if (!form.tel) {
    errors.value.tel = "Bitte Telefon eingeben";
    hasErrors = true;
  }
  if (!form.gender) {
    errors.value.gender = "Bitte Geschlecht auswählen";
    hasErrors = true;
  }
  if (!form.is_first_visit) {
    errors.value.is_first_visit = "Bitte beantworten";
    hasErrors = true;
  }

  if (!hasErrors) {
    let url = route("widget.iframe.submit", { key: props.widgetkey });
    form.post(url);
  }
}

function backClicked() {
  backUrl.value = window.history.back();
}
</script>

<template>
  <WidgetLayout :office="props.office">
    <div class="lg:flex mb-8">
      <div class="lg:w-1/2 lg:pr-16">
        <!-- left -->
        <div class="mb-8">
          <h2 class="text-3xl font-semibold mb-2">Meine Daten</h2>
        </div>
      </div>

      <div class="lg:w-1/2 lg:pl-16">
        <!-- right -->
        <div class="mb-8">
          <div class="mb-4">
            <div class="text-sm">Ausgewählter Termin:</div>
            <div class="text-medium">
              <span class="font-semibold"
                >{{ formatDate(props.timeslot[0]) }},</span
              >
              {{ formatTime(props.timeslot[0]) }}-
              {{ formatTime(props.timeslot[1]) }} Uhr
            </div>
          </div>

          <div class="mb-4">
            <div class="text-sm">Behandlungstyp:</div>
            <div class="text-medium">
              <span class="font-semibold">{{ props.type.name }}</span>
            </div>
          </div>

          <div class="mb-4">
            <div class="text-sm">Behandler:</div>
            <div class="text-medium">
              <span class="font-semibold">{{
                props.practitioner.fullname
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="lg:flex">
      <div class="lg:w-1/2 lg:pr-16 lg:border-r">
        <!-- left -->
        <div class="mb-8">
          <Label for="firstname" value="Vorname" />
          <Input
            id="firstname"
            type="text"
            class="mt-1 block w-full"
            v-model="form.firstname"
            required
            placeholder="Bitte eingeben"
          />
          <div class="mt-2 text-red-600 text-sm" v-if="errors.firstname">
            {{ errors.firstname }}
          </div>
        </div>

        <div class="mb-8">
          <Label for="lastname" value="Nachname" />
          <Input
            id="lastname"
            type="text"
            class="mt-1 block w-full"
            v-model="form.lastname"
            required
            placeholder="Bitte eingeben"
          />
          <div class="mt-2 text-red-600 text-sm" v-if="errors.lastname">
            {{ errors.lastname }}
          </div>
        </div>

        <div class="mb-8">
          <Label for="birthday" value="Geburtstag" />
          <Input
            v-model="form.birthday"
            class="w-full"
            :max="new Date().toISOString().split('T')[0]"
            type="date"
          />
          <div class="mt-2 text-red-600 text-sm" v-if="errors.birthday">
            {{ errors.birthday }}
          </div>
        </div>

        <div class="mb-8">
          <Label for="email" value="E-Mail" />
          <Input
            id="email"
            type="email"
            class="mt-1 block w-full"
            v-model="form.email"
            required
            placeholder="Bitte eingeben"
          />
          <div class="mt-2 text-red-600 text-sm" v-if="errors.email">
            {{ errors.email }}
          </div>
        </div>

        <div class="mb-8">
          <Label for="tel" value="Telefon" />
          <Input
            id="tel"
            type="tel"
            class="mt-1 block w-full"
            v-model="form.tel"
            required
            placeholder="Bitte eingeben"
          />
          <div class="mt-2 text-red-600 text-sm" v-if="errors.tel">
            {{ errors.tel }}
          </div>
        </div>
      </div>

      <div class="lg:w-1/2 lg:pl-16">
        <!-- right -->

        <div class="mb-8">
          <Label for="gender" value="Geschlecht" />
          <!-- <RadioButtons
                        v-model="form.gender"
                        :name="'gender'"
                        :options="{1: 'Weiblich', 2: 'Männlich', 3: 'Divers'}"
                    /> -->

          <div class="flex items-center">
            <input
              type="radio"
              id="gender1"
              name="gender"
              value="1"
              v-model="form.gender"
              class="mr-2"
            />
            <label for="gender1" class="mr-8">Weiblich</label>

            <input
              type="radio"
              id="gender2"
              name="gender"
              value="2"
              v-model="form.gender"
              class="mr-2"
            />
            <label for="gender2" class="mr-8">Männlich</label>

            <input
              type="radio"
              id="gender3"
              name="gender"
              value="3"
              v-model="form.gender"
              class="mr-2"
            />
            <label for="gender3">Divers</label>
          </div>
          <div class="mt-2 text-red-600 text-sm" v-if="errors.gender">
            {{ errors.gender }}
          </div>
        </div>

        <div class="mb-8">
          <Label
            for="is_first_visit"
            value="Waren Sie bereits in unserer Praxis?"
          />
          <!-- <RadioButtons
                        v-model="form.is_first_visit"
                        :name="'is_first_visit'"
                        :options="{1: 'Ja', 0: 'Nein'}"
                    /> -->

          <div class="flex items-center">
            <input
              type="radio"
              id="is_first_visit1"
              name="is_first_visit"
              value="1"
              v-model="form.is_first_visit"
              class="mr-2"
            />
            <label for="is_first_visit1" class="mr-8">Nein</label>

            <input
              type="radio"
              id="is_first_visit2"
              name="is_first_visit"
              value="0"
              v-model="form.is_first_visit"
              class="mr-2"
            />
            <label for="is_first_visit2">Ja</label>
          </div>
          <div class="mt-2 text-red-600 text-sm" v-if="errors.is_first_visit">
            {{ errors.is_first_visit }}
          </div>
        </div>

        <div class="mb-8">
          <Label for="notes" value="Hinweis an die Praxis" />
          <Textarea
            id="notes"
            class="mt-1 block w-full resize-none"
            style="height: 17rem"
            v-model="form.notes"
            placeholder="Bitte eingeben"
          />
          <div class="mt-2 text-red-600 text-sm" v-if="errors.notes">
            {{ errors.notes }}
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 text-red-600 text-sm" v-if="errors.timeslot">
      {{ errors.timeslot }}
    </div>

    <!--<div class="mt-8">
            <a @click="getFreeSlots()">Test</a>
        </div>-->

    <div class="mt-12 py-6 flex justify-between">
      <a
        class="px-6 md:px-20 py-3 text-xl text-gray-500 bg-transparent hover:bg-gray-200 border border-gray-500 rounded-lg cursor-pointer"
        @click="backClicked"
        :href="backUrl"
        >Zurück</a
      >

      <button
        class="px-6 md:px-20 py-3 text-xl font-semibold rounded-lg text-white bg-primary hover:opacity-70 cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
        @click="submitForm()"
      >
        Termin anfragen</button
      ><!-- :disabled="!form.slotData" -->
    </div>
  </WidgetLayout>
</template>

<style>
:root {
  --spitta-primary: 0 110 180;
  --spitta-primary-hex: #006eb4;
  --color-primary: var(--spitta-primary);
}

.bg-primary {
  background-color: #006eb4;
}
.vc-container .vc-highlight {
  background-color: #006eb4;
}

.vc-day .is-disabled {
  pointer-events: none;
}
</style>
